import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Button,
  FormField,
  Input,
  Select,
  DatePicker,
  SpaceBetween,
  Box,
  SelectProps,
} from '@cloudscape-design/components';
import { useFormValidation } from 'src/hooks/useFormValidation';
import { AuditValidator, AuditValidationPaths } from 'src/validations/AuditValidator';
import { useAudits } from 'src/store/audits/useAudits';
import { useTemplates } from 'src/store/templates/useTemplates';
import useUserProfile from 'src/hooks/useUserProfile';
import RequiredFieldLabel from '../RequiredFieldLabel';
import { CreateAuditRequest } from 'src/types/helios/Audit';
import ApiStatusFlashBar from '../ApiStatusFlashBar';
import { useDivision } from 'src/store/division/useDivision';
import { useLocations } from 'src/store/locations/useLocations';
import { Division } from 'src/types/orbit/Division';
import SingleSelectDivision from '../division/SingleSelectDivision';
import { AuditTemplate } from '@amzn/gbsa-helios-typescript-client';
import {
  convertItem,
  convertList,
  formatToISOWithMidnight,
  formatToISOWithEndOfDay,
  getAuditTypeOptions,
  parseAssignees,
} from 'src/utils/generalUtils';
import { Location } from 'src/types/helios/Location';
import AuditTypeSelect from './AuditTypeSelect';
import { toLower } from 'lodash';

interface CreateAuditModalProps {
  visible: boolean; // Modal visibility state
  isEditTestAudit: boolean; // Permission for test audit creation
  isEditProgressAllow: boolean; // Permission for progress audit creation
  isEditSituationAllow: boolean; // Permission for situation audit creation
  isEditCalibrationAllow: boolean; // Permission for caliberation audit creation
  isEditMpaAllow: boolean; // Permission for MPA audit creation
  onDismiss: () => void; // Callback when modal is dismissed
}

interface AuditFormData {
  name: string; // Audit name
  location: string; // Location code
  division: string; // Division name
  auditType: string; // Type of audit
  startDate: string; // Audit start date
  expiryDate: string; // Audit expiry date
  templateId: string; // Selected template ID
  assignees: string; // Assigned users (comma-separated raw input)
}

/**
 * CreateAuditModal - Modal form for creating new audits
 * Handles form validation, data loading, and audit creation with permission-based field access
 */
function CreateAuditModal({
  visible,
  onDismiss,
  isEditProgressAllow,
  isEditTestAudit,
  isEditSituationAllow,
  isEditCalibrationAllow,
  isEditMpaAllow,
}: CreateAuditModalProps) {
  // Custom hooks for data management
  const { getUserId, getUserBusinessUnitId, getUserProgramId } = useUserProfile();
  const { createAudit, createAuditApiStatus, resetApiStatus } = useAudits();
  const { templatesList, loadTemplatesList } = useTemplates();
  const { divisionList, fetcDivisionsApiStatus, loadDivisionsByBusinessUnit } =
    useDivision();
  const { locationsList, loadLocations } = useLocations();

  // Initial form values
  const initValues = {
    name: '',
    location: '',
    division: '',
    auditType: '',
    startDate: '',
    expiryDate: '',
    templateId: '',
    assignees: '',
  };

  // Form state and validation
  const [formData, setFormData] = useState<AuditFormData>(initValues);
  const { fieldValidationError, validate, setFieldValidationErrors } = useFormValidation(
    formData,
    AuditValidator(),
  );

  // Close modal on successful audit creation
  useEffect(() => {
    if (createAuditApiStatus.isSuccess) {
      onDismiss();
    }
  }, [createAuditApiStatus.isSuccess, onDismiss]);

  // Load required data when modal opens and cleanup on close
  useEffect(() => {
    if (visible) {
      loadTemplatesList({
        businessUnitId: getUserBusinessUnitId(),
        program: getUserProgramId(),
      });
      loadDivisionsByBusinessUnit({
        businessUnitId: getUserBusinessUnitId(),
        programId: getUserProgramId(),
        isActive: true,
        isDeleted: false,
      });
      loadLocations({
        businessUnitId: getUserBusinessUnitId(),
      });
    }
    return () => {
      resetAll();
    };
  }, [visible]);

  /**
   * Resets form data, validation errors, and API status
   */
  const resetAll = () => {
    setFormData(initValues);
    setFieldValidationErrors({});
    resetApiStatus();
  };

  /**
   * Handles audit creation after form validation
   */
  const handleCreateAudit = () => {
    if (validate()) {
      const createRequest: CreateAuditRequest = {
        businessUnitId: getUserBusinessUnitId(),
        templateId: formData.templateId,
        location: formData.location,
        scheduledDate: formatToISOWithMidnight(formData.startDate),
        startDate: formatToISOWithMidnight(formData.startDate),
        auditType: formData.auditType.toUpperCase(),
        name: formData.name,
        divisionName: formData.division,
        assignees: formData.assignees ? parseAssignees(formData.assignees) : undefined,
        createdBy: getUserId(),
        expirationDate: formatToISOWithEndOfDay(formData.expiryDate),
      };
      createAudit(createRequest);
    }
  };

  // Memoized template options filtered by selected location
  const templateOptions = useMemo(
    () =>
      templatesList
        .filter(
          (item) =>
            (item.locationsIncluded || []).includes(formData.location) &&
            item.divisionName === formData.division,
        )
        .map((template) => ({
          value: template.id,
          label: template.templateName,
        })),
    [templatesList, formData.location, formData.division],
  );

  // Memoized selected template option for the dropdown
  const selectedTemplate = useMemo(() => {
    if (formData.templateId === '' || templatesList.length === 0) return null;
    return convertItem<AuditTemplate, SelectProps.Option>(
      templatesList.find((data) => formData.templateId === data.id)!,
      (item) => ({
        value: item.id,
        label: item.templateName,
      }),
    );
  }, [formData.templateId, templatesList]);

  // Memoized selected location option for the dropdown
  const selectedLocation = useMemo(() => {
    if (formData.location === '' || locationsList.length === 0) return null;
    return convertItem<Location, SelectProps.Option>(
      locationsList.find((data) => formData.location === data.locationCode)!,
      (item) => ({
        value: item.locationCode,
        label: item.locationCode,
      }),
    );
  }, [formData.location, locationsList]);

  return (
    <Modal
      data-testid="create-audit-modal"
      visible={visible}
      onDismiss={onDismiss}
      header="Create New Audit"
      size="medium"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button data-testid="cancel-button" variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              data-testid="save-button"
              variant="primary"
              onClick={handleCreateAudit}
              loading={createAuditApiStatus.isLoading}
            >
              Create Audit
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <ApiStatusFlashBar
          apiStatus={[createAuditApiStatus]}
          clearMessages={() => resetApiStatus()}
        />

        <FormField
          data-testid="audit-name-field"
          label={<RequiredFieldLabel label="Audit Name" />}
          errorText={fieldValidationError[AuditValidationPaths.NAME.path]}
        >
          <Input
            data-testid="audit-name-input"
            value={formData.name}
            onChange={({ detail }) => setFormData({ ...formData, name: detail.value })}
            placeholder="Enter audit name"
          />
        </FormField>
        <FormField
          label={<RequiredFieldLabel label="Location" />}
          data-testid="location-field"
          errorText={fieldValidationError[AuditValidationPaths.LOCATION.path]}
        >
          <Select
            filteringType="auto"
            data-testid="location-dropdown"
            options={convertList<Location, SelectProps.Option>(locationsList, (item) => ({
              value: item.locationCode,
              label: item.locationCode,
            }))}
            selectedOption={selectedLocation}
            onChange={({ detail }) => {
              setFormData((prevState) => ({
                ...prevState,
                location: detail.selectedOption.value || '',
                division: '',
                templateId: '',
              }));
            }}
            loadingText="Loading Location"
            placeholder="Choose Location"
          />
        </FormField>
        <FormField
          data-testid="division-field"
          label={<RequiredFieldLabel label="Division" />}
          errorText={fieldValidationError[AuditValidationPaths.DIVISION.path]}
        >
          <SingleSelectDivision
            data-testid="division-select"
            divisions={divisionList}
            isLoading={fetcDivisionsApiStatus.isLoading}
            error={fetcDivisionsApiStatus.errorMessage}
            selectedDivision={formData.division}
            onItemSelection={(item: Division | null) => {
              setFormData((prevState) => ({
                ...prevState,
                division: item?.divisionName || '',
                templateId: '',
              }));
            }}
          />
        </FormField>

        <FormField
          data-testid="template-field"
          label={<RequiredFieldLabel label="Template" />}
          errorText={fieldValidationError[AuditValidationPaths.TEMPLATE_ID.path]}
        >
          <Select
            data-testid="template-select"
            selectedOption={selectedTemplate}
            onChange={({ detail }) =>
              setFormData((prevState) => ({
                ...prevState,
                templateId: convertItem<SelectProps.Option, string>(
                  detail.selectedOption as SelectProps.Option,
                  (item) => item.value || '',
                ),
              }))
            }
            filteringType="auto"
            options={templateOptions}
            placeholder="Select templates"
          />
        </FormField>

        <FormField
          data-testid="assignee-field"
          label={<RequiredFieldLabel label="Assignees" />}
          description="Enter comma-separated aliases (e.g. user1, user2)"
          errorText={fieldValidationError[AuditValidationPaths.ASSIGNEE.path]}
        >
          <Input
            data-testid="assignee-input"
            value={formData.assignees}
            onChange={({ detail }) =>
              setFormData({ ...formData, assignees: toLower(detail.value) })
            }
            placeholder="Enter assignee aliases, separated by commas"
          />
        </FormField>

        <FormField
          data-testid="audit-type-field"
          label={<RequiredFieldLabel label="Audit Type" />}
          errorText={fieldValidationError[AuditValidationPaths.AUDIT_TYPE.path]}
        >
          <AuditTypeSelect
            data-testid="audit-type-select"
            selectedAuditType={formData.auditType}
            onSelectionChange={(auditType: string) =>
              setFormData((prevState) => ({
                ...prevState,
                auditType: auditType,
              }))
            }
            options={getAuditTypeOptions({
              isEditCalibrationAllow: isEditCalibrationAllow,
              isEditProgressAllow: isEditProgressAllow,
              isEditSituationAllow: isEditSituationAllow,
              isEditTestAudit: isEditTestAudit,
              isEditMpaAllow: isEditMpaAllow,
            })}
          />
        </FormField>

        <SpaceBetween direction="horizontal" size="xl">
          <FormField
            data-testid="start-date-field"
            label={<RequiredFieldLabel label="Audit Start Date" />}
            errorText={fieldValidationError[AuditValidationPaths.START_DATE.path]}
          >
            <DatePicker
              data-testid="start-date-picker"
              value={formData.startDate}
              isDateEnabled={(date) => date >= new Date(new Date().setHours(0, 0, 0, 0))}
              onChange={({ detail }) =>
                setFormData({ ...formData, startDate: detail.value, expiryDate: '' })
              }
              placeholder="YYYY/MM/DD"
            />
          </FormField>

          <FormField
            data-testid="expiry-date-field"
            label={<RequiredFieldLabel label="Expiry Date" />}
            errorText={fieldValidationError[AuditValidationPaths.EXPIRY_DATE.path]}
          >
            <DatePicker
              data-testid="expiry-date-picker"
              value={formData.expiryDate}
              onChange={({ detail }) =>
                setFormData({ ...formData, expiryDate: detail.value })
              }
              isDateEnabled={(date) => {
                if (formData?.startDate) {
                  return (
                    date > new Date(new Date(formData?.startDate).setHours(0, 0, 0, 0))
                  );
                }
                return date > new Date(new Date().setHours(0, 0, 0, 0));
              }}
              placeholder="YYYY/MM/DD"
            />
          </FormField>
        </SpaceBetween>
      </SpaceBetween>
    </Modal>
  );
}

export default CreateAuditModal;
